import React from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Lock, Delete, CheckCircle2, XCircle, LogIn, LogOut, ArrowLeft } from 'lucide-react';
import { publicKioskApi } from '../../lib/api';

interface KioskEmployee {
    id: string;
    first_name: string;
    last_name: string;
    has_open: number | boolean;
}

const storageKey = (clientId: string) => `kiosk_code_${clientId}`;

export const PointageKiosque: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const [code, setCode] = React.useState('');
    const [unlocked, setUnlocked] = React.useState(false);
    const [companyName, setCompanyName] = React.useState('');
    const [unlockError, setUnlockError] = React.useState('');
    const [unlocking, setUnlocking] = React.useState(false);

    const [employees, setEmployees] = React.useState<KioskEmployee[]>([]);
    const [selected, setSelected] = React.useState<KioskEmployee | null>(null);
    const [pin, setPin] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [pinError, setPinError] = React.useState('');
    const [result, setResult] = React.useState<{ status: 'clocked_in' | 'clocked_out'; first_name: string } | null>(null);
    const [clock, setClock] = React.useState(new Date());

    React.useEffect(() => {
        const t = setInterval(() => setClock(new Date()), 1000 * 30);
        return () => clearInterval(t);
    }, []);

    const loadEmployees = React.useCallback((c: string) => {
        if (!clientId) return;
        publicKioskApi.getEmployees(clientId, c).then((d: any) => setEmployees(Array.isArray(d) ? d : [])).catch(() => setEmployees([]));
    }, [clientId]);

    React.useEffect(() => {
        if (!clientId) return;
        const saved = localStorage.getItem(storageKey(clientId));
        if (saved) {
            publicKioskApi.unlock(clientId, saved).then((res: any) => {
                if (res?.success) {
                    setCode(saved);
                    setCompanyName(res.company_name || '');
                    setUnlocked(true);
                    loadEmployees(saved);
                } else {
                    localStorage.removeItem(storageKey(clientId));
                }
            }).catch(() => {});
        }
    }, [clientId, loadEmployees]);

    const handleUnlock = async () => {
        if (!clientId || !code.trim()) return;
        setUnlocking(true);
        setUnlockError('');
        try {
            const res: any = await publicKioskApi.unlock(clientId, code.trim());
            if (res?.success) {
                localStorage.setItem(storageKey(clientId), code.trim());
                setCompanyName(res.company_name || '');
                setUnlocked(true);
                loadEmployees(code.trim());
            } else {
                setUnlockError(res?.error || 'Code invalide.');
            }
        } catch {
            setUnlockError('Erreur de connexion.');
        } finally {
            setUnlocking(false);
        }
    };

    const selectEmployee = (e: KioskEmployee) => {
        setSelected(e);
        setPin('');
        setPinError('');
    };

    const pressDigit = (d: string) => {
        if (pin.length >= 4) return;
        setPin((p) => p + d);
    };
    const backspace = () => setPin((p) => p.slice(0, -1));

    const handleValidate = React.useCallback(async () => {
        if (!clientId || !selected || pin.length !== 4 || submitting) return;
        setSubmitting(true);
        setPinError('');
        try {
            const res: any = await publicKioskApi.toggle(clientId, code, selected.id, pin);
            if (res?.status) {
                setResult({ status: res.status, first_name: res.first_name || selected.first_name });
                setSelected(null);
                setPin('');
                loadEmployees(code);
                setTimeout(() => setResult(null), 2500);
            } else {
                setPinError(res?.error || 'Code PIN incorrect.');
                setPin('');
            }
        } catch {
            setPinError('Erreur de connexion.');
            setPin('');
        } finally {
            setSubmitting(false);
        }
    }, [clientId, selected, pin, code, submitting, loadEmployees]);

    React.useEffect(() => {
        if (pin.length === 4) handleValidate();
    }, [pin, handleValidate]);

    if (!clientId) return null;

    if (!unlocked) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-6">
                <div className="w-full max-w-sm text-center">
                    <Lock size={36} className="mx-auto mb-4 text-white/40" />
                    <h1 className="text-white text-xl font-bold mb-1">Pointage — Tablette</h1>
                    <p className="text-white/50 text-sm mb-6">Entrez le code d'accès de l'établissement.</p>
                    <input
                        type="password"
                        inputMode="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                        placeholder="Code d'accès"
                        autoFocus
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-center text-white text-lg outline-none focus:border-white/50 mb-3"
                    />
                    {unlockError && <p className="text-red-400 text-sm mb-3">{unlockError}</p>}
                    <button onClick={handleUnlock} disabled={unlocking || !code.trim()}
                        className="w-full py-3.5 rounded-xl bg-white text-black font-bold disabled:opacity-40 transition-all">
                        {unlocking ? 'Vérification...' : 'Déverrouiller'}
                    </button>
                </div>
            </div>
        );
    }

    if (result) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-6">
                <div className="text-center">
                    {result.status === 'clocked_in' ? (
                        <>
                            <LogIn size={64} className="mx-auto mb-4 text-emerald-400" />
                            <p className="text-white text-2xl font-bold">{result.first_name}, service commencé</p>
                        </>
                    ) : (
                        <>
                            <LogOut size={64} className="mx-auto mb-4 text-amber-400" />
                            <p className="text-white text-2xl font-bold">{result.first_name}, service terminé</p>
                        </>
                    )}
                    <p className="text-white/40 text-sm mt-2">{clock.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
            </div>
        );
    }

    if (selected) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
                <button onClick={() => { setSelected(null); setPin(''); setPinError(''); }} className="absolute top-6 left-6 text-white/50 hover:text-white flex items-center gap-1.5 text-sm">
                    <ArrowLeft size={16} /> Retour
                </button>
                <p className="text-white/50 text-sm mb-1">Code PIN de</p>
                <p className="text-white text-2xl font-bold mb-6">{selected.first_name} {selected.last_name}</p>
                <div className="flex gap-3 mb-8">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={`w-4 h-4 rounded-full border-2 ${pin.length > i ? 'bg-white border-white' : 'border-white/30'}`} />
                    ))}
                </div>
                {pinError && <p className="text-red-400 text-sm mb-4">{pinError}</p>}
                {submitting && <p className="text-white/50 text-sm mb-4">Vérification...</p>}
                <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                        <button key={d} onClick={() => pressDigit(d)} disabled={submitting}
                            className="aspect-square rounded-2xl bg-white/10 hover:bg-white/20 text-white text-2xl font-bold transition-all disabled:opacity-40">
                            {d}
                        </button>
                    ))}
                    <div />
                    <button onClick={() => pressDigit('0')} disabled={submitting}
                        className="aspect-square rounded-2xl bg-white/10 hover:bg-white/20 text-white text-2xl font-bold transition-all disabled:opacity-40">0</button>
                    <button onClick={backspace} disabled={submitting}
                        className="aspect-square rounded-2xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all disabled:opacity-40">
                        <Delete size={22} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black p-6">
            <header className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-white text-xl font-bold flex items-center gap-2"><Clock size={20} /> {companyName}</h1>
                    <p className="text-white/40 text-sm mt-0.5">Touchez votre nom pour pointer.</p>
                </div>
                <p className="text-white/50 text-sm">{clock.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
            </header>
            {employees.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-16">Aucun salarié n'a encore de code PIN configuré.</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {employees.map((e) => (
                        <button key={e.id} onClick={() => selectEmployee(e)}
                            className="p-5 rounded-2xl bg-white/10 hover:bg-white/20 text-left transition-all">
                            <p className="text-white font-bold">{e.first_name} {e.last_name}</p>
                            <p className={`text-xs mt-1 flex items-center gap-1.5 ${e.has_open ? 'text-emerald-400' : 'text-white/40'}`}>
                                {e.has_open ? <><CheckCircle2 size={12} /> En service</> : <><XCircle size={12} /> Hors service</>}
                            </p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PointageKiosque;
